import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  PointOfSale as SalesIcon,
  AccountBalanceWallet as RechargeIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import reportAPI, {
  RechargeReportSortField,
  SalesReportFilter,
  SalesReportSortField,
} from "../../services/reportAPI";
import financialYearAPI, {
  FinancialYear,
  formatFinancialYearDisplay,
  indiaFyMeta,
} from "../../services/financialYearAPI";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatPaymentMethod = (method: string) => {
  const map: Record<string, string> = {
    cash: "Cash",
    card: "Card",
    upi: "UPI",
    netbanking: "Net Banking",
  };
  return map[String(method || "").toLowerCase()] || method || "Other";
};

const defaultStart = () => indiaFyMeta().startDate;
const defaultEnd = () => {
  const today = format(new Date(), "yyyy-MM-dd");
  const fyEnd = indiaFyMeta().endDate;
  return today < fyEnd ? today : fyEnd;
};

const MyReportsPage: React.FC = () => {
  const { user } = useAuthStore();
  const { hasAnyRole } = useRBAC();
  const isOperator = hasAnyRole("OPERATOR");
  const showRechargeTab = isOperator;

  const [tab, setTab] = useState<"sales" | "recharges">("sales");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [draft, setDraft] = useState<SalesReportFilter>({
    startDate: defaultStart(),
    endDate: defaultEnd(),
    fyCode: indiaFyMeta().code,
  });
  const [filters, setFilters] = useState<SalesReportFilter>(draft);

  const { data: financialYears = [] } = useQuery({
    queryKey: ["financialYears"],
    queryFn: () => financialYearAPI.list(),
  });
  const {
    sortBy: salesSortBy,
    sortOrder: salesSortOrder,
    handleSort: handleSalesSort,
    sortParams: salesSortParams,
  } = useTableSort<SalesReportSortField>("createdAt", "desc");
  const {
    sortBy: rechargeSortBy,
    sortOrder: rechargeSortOrder,
    handleSort: handleRechargeSort,
    sortParams: rechargeSortParams,
  } = useTableSort<RechargeReportSortField>("createdAt", "desc");

  const salesQueryKey = useMemo(
    () => ["my-sales-report", filters, page, rowsPerPage, salesSortBy, salesSortOrder],
    [filters, page, rowsPerPage, salesSortBy, salesSortOrder]
  );

  const rechargeQueryKey = useMemo(
    () => ["my-recharges-report", filters, page, rowsPerPage, rechargeSortBy, rechargeSortOrder],
    [filters, page, rowsPerPage, rechargeSortBy, rechargeSortOrder]
  );

  const {
    data: salesData,
    isLoading: salesLoading,
    error: salesError,
    isFetching: salesFetching,
  } = useQuery({
    queryKey: salesQueryKey,
    queryFn: () =>
      reportAPI.getSalesReport({
        ...filters,
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...salesSortParams,
      }),
    enabled: tab === "sales" && Boolean(filters.startDate && filters.endDate),
    staleTime: 0,
  });

  const {
    data: rechargeData,
    isLoading: rechargeLoading,
    error: rechargeError,
    isFetching: rechargeFetching,
  } = useQuery({
    queryKey: rechargeQueryKey,
    queryFn: () =>
      reportAPI.getRechargesReport({
        startDate: filters.startDate,
        endDate: filters.endDate,
        branchId: filters.branchId,
        salesPointId: filters.salesPointId,
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...rechargeSortParams,
      }),
    enabled:
      tab === "recharges" &&
      showRechargeTab &&
      Boolean(filters.startDate && filters.endDate),
    staleTime: 0,
  });

  const applyFilters = () => {
    setPage(0);
    setFilters({ ...draft });
  };

  const onSalesSort = (field: SalesReportSortField) =>
    handleSalesSort(field, () => setPage(0));
  const onRechargeSort = (field: RechargeReportSortField) =>
    handleRechargeSort(field, () => setPage(0));

  const handleTabChange = (_: React.SyntheticEvent, value: "sales" | "recharges") => {
    setTab(value);
    setPage(0);
  };

  const activeError = tab === "sales" ? salesError : rechargeError;
  const activeLoading = tab === "sales" ? salesLoading : rechargeLoading;
  const isFetching = tab === "sales" ? salesFetching : rechargeFetching;

  const errorMsg =
    activeError instanceof Error
      ? activeError.message
      : (activeError as any)?.response?.data?.message || "Failed to load report";

  return (
    <Box>
      <PageHeader
        title="My Reports"
        subtitle={
          isOperator
            ? "Your sales and recharges — payment method wise summary"
            : "Your sales summary and transaction history"
        }
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "My Reports" },
        ]}
      />

      <Paper sx={{ p: 1.5, mb: 1.5, border: "1px solid", borderColor: "divider" }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Financial year</InputLabel>
              <Select
                label="Financial year"
                value={draft.fyCode || ""}
                onChange={(e) => {
                  const code = e.target.value || undefined;
                  const fy =
                    (financialYears as FinancialYear[]).find((f) => f.code === code) ||
                    null;
                  const meta = fy
                    ? {
                        startDate: String(fy.startDate).slice(0, 10),
                        endDate: String(fy.endDate).slice(0, 10),
                      }
                    : indiaFyMeta();
                  const today = format(new Date(), "yyyy-MM-dd");
                  setDraft((p) => ({
                    ...p,
                    fyCode: code,
                    startDate: meta.startDate,
                    endDate: today < meta.endDate ? today : meta.endDate,
                  }));
                }}
              >
                {(financialYears as FinancialYear[]).length > 0 ? (
                  (financialYears as FinancialYear[]).map((fy) => (
                    <MenuItem key={fy.id} value={fy.code}>
                      {formatFinancialYearDisplay(fy)}
                      {fy.isCurrent ? " (current)" : ""}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value={indiaFyMeta().code}>
                    {indiaFyMeta().display} (current)
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              value={draft.startDate}
              onChange={(e) => setDraft((p) => ({ ...p, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              value={draft.endDate}
              onChange={(e) => setDraft((p) => ({ ...p, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={applyFilters}
              disabled={isFetching}
            >
              View report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab icon={<SalesIcon />} iconPosition="start" label="My Sales" value="sales" />
        {showRechargeTab && (
          <Tab
            icon={<RechargeIcon />}
            iconPosition="start"
            label="My Recharges"
            value="recharges"
          />
        )}
      </Tabs>

      {activeError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {activeLoading && (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton height={80} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton height={300} />
          </Grid>
        </Grid>
      )}

      {!activeLoading && tab === "sales" && salesData && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip
              size="small"
              label={`${format(new Date(filters.startDate), "dd MMM yyyy")} – ${format(new Date(filters.endDate), "dd MMM yyyy")}`}
            />
            {user?.name && (
              <Chip size="small" color="primary" label={`Operator: ${user.name}`} />
            )}
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: "Total sales", value: salesData.summary.totalSales },
              {
                label: "Total amount",
                value: formatCurrency(salesData.summary.totalAmount),
                isText: true,
              },
              {
                label: "Net amount",
                value: formatCurrency(salesData.summary.totalNet),
                isText: true,
              },
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item.label}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {item.isText ? item.value : item.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {salesData.byPaymentMethod.length > 0 && (
            <Paper sx={{ p: 2, mb: 3, border: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Sales by payment method
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment method</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Net amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.byPaymentMethod.map((row) => (
                      <TableRow key={row.paymentMethod}>
                        <TableCell>{formatPaymentMethod(row.paymentMethod)}</TableCell>
                        <TableCell align="right">{row.saleCount}</TableCell>
                        <TableCell align="right">{formatCurrency(row.totalNet)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Paper sx={{ border: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2, pb: 0 }}>
              What you sold
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell label="Date" field="createdAt" sortBy={salesSortBy} sortOrder={salesSortOrder} onSort={onSalesSort} />
                    <SortableTableCell label="Sale #" field="saleNumber" sortBy={salesSortBy} sortOrder={salesSortOrder} onSort={onSalesSort} />
                    <TableCell>Items</TableCell>
                    <SortableTableCell label="Payment" field="paymentMethod" sortBy={salesSortBy} sortOrder={salesSortOrder} onSort={onSalesSort} />
                    <SortableTableCell label="Amount" field="netAmount" sortBy={salesSortBy} sortOrder={salesSortOrder} onSort={onSalesSort} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesData.sales.map((sale) => {
                    const items = sale.saleItems ?? [];
                    const itemSummary =
                      items.length === 0
                        ? "—"
                        : items
                            .map((item) => {
                              const name = item.product?.name || "Item";
                              return `${name} × ${item.quantity}`;
                            })
                            .join(", ");
                    return (
                      <TableRow key={sale.id} hover>
                        <TableCell>
                          {format(new Date(sale.createdAt), "dd MMM yyyy HH:mm")}
                        </TableCell>
                        <TableCell>{sale.saleNumber || sale.id.slice(0, 8)}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" noWrap title={itemSummary}>
                            {itemSummary}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatPaymentMethod(sale.paymentMethod)}</TableCell>
                        <TableCell align="right">{formatCurrency(sale.netAmount)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {salesData.sales.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        No sales found for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50]}
              count={salesData.pagination.totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </>
      )}

      {!activeLoading && tab === "recharges" && rechargeData && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip
              size="small"
              label={`${format(new Date(filters.startDate), "dd MMM yyyy")} – ${format(new Date(filters.endDate), "dd MMM yyyy")}`}
            />
            {user?.name && (
              <Chip size="small" color="primary" label={`Operator: ${user.name}`} />
            )}
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total recharges
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {rechargeData.summary.totalRecharges}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total amount
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {formatCurrency(rechargeData.summary.totalAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {rechargeData.byPaymentMethod.length > 0 && (
            <Paper sx={{ p: 2, mb: 3, border: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Recharges by payment method
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment method</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rechargeData.byPaymentMethod.map((row) => (
                      <TableRow key={row.paymentMethod}>
                        <TableCell>{formatPaymentMethod(row.paymentMethod)}</TableCell>
                        <TableCell align="right">{row.rechargeCount}</TableCell>
                        <TableCell align="right">{formatCurrency(row.totalAmount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Paper sx={{ border: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2, pb: 0 }}>
              Recharge history
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell label="Date" field="createdAt" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={onRechargeSort} />
                    <SortableTableCell label="Card holder" field="holderName" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={onRechargeSort} />
                    <SortableTableCell label="Phone" field="phone" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={onRechargeSort} />
                    <SortableTableCell label="Payment" field="paymentMethod" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={onRechargeSort} />
                    <SortableTableCell label="Amount" field="amount" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={onRechargeSort} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rechargeData.recharges.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {format(new Date(row.createdAt), "dd MMM yyyy HH:mm")}
                      </TableCell>
                      <TableCell>{row.cardHolder?.holderName || "—"}</TableCell>
                      <TableCell>{row.cardHolder?.phone || "—"}</TableCell>
                      <TableCell>{formatPaymentMethod(row.paymentMethod)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {rechargeData.recharges.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        No recharges found for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50]}
              count={rechargeData.pagination.totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MyReportsPage;
